import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import {
  FileText,
  Camera,
  Calendar,
  Settings,
  CreditCard,
  Shield,
  Mail,
  Sparkles,
  Home,
  DollarSign,
  Users,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_PAGES = [
  { title: 'Dashboard', path: '/dashboard', icon: Home, keywords: 'home main overview' },
  { title: 'Write a Letter', path: '/letters', icon: FileText, keywords: 'new letter compose create draft ai' },
  { title: 'Memory Photos', path: '/photos', icon: Camera, keywords: 'gallery upload generate ai image' },
  { title: 'Letter Calendar', path: '/calendar', icon: Calendar, keywords: 'streak history activity days' },
  { title: 'Settings', path: '/settings', icon: Settings, keywords: 'preferences digest email notifications' },
  { title: 'Billing & Subscription', path: '/pricing', icon: CreditCard, keywords: 'pricing plans upgrade payment stripe' },
  { title: 'Trust & Compliance', path: '/compliance', icon: Shield, keywords: 'policy ai disclaimer safety' },
];

const ADMIN_PAGES = [
  { title: 'Admin Panel', path: '/admin', icon: Users, keywords: 'users manage subscriptions admin' },
];

export default function SearchPalette({ open, onOpenChange, isAdmin }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ letters: [], photos: [] });
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults({ letters: [], photos: [] });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/search`, {
        params: { q },
        withCredentials: true
      });
      setResults({ letters: data.letters || [], photos: data.photos || [] });
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => doSearch(query), 200);
    return () => clearTimeout(timer);
  }, [query, doSearch]);

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const handleSelect = (path) => {
    onOpenChange(false);
    setQuery('');
    navigate(path);
  };

  const pages = isAdmin ? [...NAV_PAGES, ...ADMIN_PAGES] : NAV_PAGES;
  const filteredPages = query
    ? pages.filter(p => {
        const q = query.toLowerCase();
        return p.title.toLowerCase().includes(q) || p.keywords.toLowerCase().includes(q);
      })
    : pages;

  const hasContent = filteredPages.length > 0 || results.letters.length > 0 || results.photos.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search letters, photos, pages..."
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList data-testid="search-results">
        {!hasContent && !loading && (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        )}
        {loading && <div className="py-4 text-center text-sm text-[#737373]">Searching...</div>}

        {filteredPages.length > 0 && (
          <CommandGroup heading="Pages">
            {filteredPages.map((page) => {
              const Icon = page.icon;
              return (
                <CommandItem
                  key={page.path}
                  value={page.title + ' ' + page.keywords}
                  onSelect={() => handleSelect(page.path)}
                  data-testid={`search-page-${page.path.slice(1) || 'home'}`}
                >
                  <Icon className="mr-2 h-4 w-4 text-[#B94A3E]" strokeWidth={1.5} />
                  <span>{page.title}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {results.letters.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Letters">
              {results.letters.map((letter) => (
                <CommandItem
                  key={letter.id}
                  value={`letter-${letter.id}`}
                  onSelect={() => handleSelect(`/letters/${letter.id}`)}
                  data-testid={`search-letter-${letter.id}`}
                >
                  <Mail className="mr-2 h-4 w-4 text-[#B94A3E]" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{letter.title}</span>
                      <span className="text-xs text-[#737373] uppercase tracking-wide">{letter.status}</span>
                    </div>
                    <p className="text-xs text-[#737373] truncate handwriting">{letter.snippet}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.photos.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Photos">
              {results.photos.map((photo) => (
                <CommandItem
                  key={photo.id}
                  value={`photo-${photo.id}`}
                  onSelect={() => handleSelect('/photos')}
                  data-testid={`search-photo-${photo.id}`}
                >
                  {photo.is_ai ? (
                    <Sparkles className="mr-2 h-4 w-4 text-[#B94A3E]" strokeWidth={1.5} />
                  ) : (
                    <Camera className="mr-2 h-4 w-4 text-[#B94A3E]" strokeWidth={1.5} />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="truncate block">{photo.prompt}</span>
                    {photo.is_ai && <span className="text-xs text-[#B94A3E]">Made with AI</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
